import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import SplitText from './components/SplitText';
import TradeManagementSystem from './components/TradeManagementSystem';
import StockMarketNewsPortal from './components/StockMarketNewsPortal';
import FoodManagementSystem from './components/FoodManagementSystem';
import Contact from './components/Contact';
import About from './components/About';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import { downloadResume } from './utils/resumeDownload';

// Home component with navigation functionality
const Home = () => {
  const navigate = useNavigate();
  const [showMobileImage, setShowMobileImage] = useState(false);

  const handleTradeSystemClick = () => {
    navigate('/trade-management-system');
  };

  const handleStockMarketClick = () => {
    navigate('/stock-market-news-portal');
  };

  const handleFoodManagementClick = () => {
    navigate('/food-management-system');
  };

  const handleResumeDownload = downloadResume;

  const toggleMobileImage = () => {
    setShowMobileImage(!showMobileImage);
  };

  return (
    <div className="App">
      {/* Header Component */}
      <Header activePage="works" />

      {/* Hero Section */}
      <main className="hero-section">
        <div className="hero-content">
          {/* Left Half - Image Section (Smaller) */}
          <div className="image-section">
            <div className="image-container">
              {/* Replace the src path with your actual image path */}
              <img 
                src="/dp.png" 
                alt="Subeksha Thapa" 
                className="profile-image"
              />
            </div>
          </div>

          {/* Right Half - Text Section (Larger) */}
          <div className="text-section">
            <div className="text-content">
              <SplitText
                text="SUBEKSHA THAPA"
                tag="h1"
                className="name"
                splitType="chars"
                delay={50}
                duration={0.8}
                from={{ opacity: 0, y: 60 }}
                to={{ opacity: 1, y: 0 }}
                textAlign="right"
              />
              <SplitText
                text="PRODUCT DESIGNER"
                tag="h2"
                className="title"
                splitType="chars"
                delay={100}
                duration={0.8}
                from={{ opacity: 0, y: 40 }}
                to={{ opacity: 1, y: 0 }}
                textAlign="right"
              />
              <SplitText
                text="UI/UX DESIGNER"
                tag="h3"
                className="subtitle"
                splitType="chars"
                delay={150}
                duration={0.8}
                from={{ opacity: 0, y: 40 }}
                to={{ opacity: 1, y: 0 }}
                textAlign="right"
              />
            </div>
            
            {/* Mobile View Image Button - Inside text-section */}
            <button className="mobile-view-image-btn" onClick={toggleMobileImage}>
              <span>{showMobileImage ? 'Hide Image' : 'View Image'}</span>
              <svg className={`mobile-toggle-arrow ${showMobileImage ? 'rotated' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </main>

      {/* Mobile Image Section - Shows/hides based on button click */}
      {showMobileImage && (
        <section className="mobile-image-section">
          <div className="mobile-image-container">
            <img 
              src="/dp.png" 
              alt="Subeksha Thapa" 
              className="mobile-profile-image"
            />
          </div>
        </section>
      )}

      {/* New Content Section */}
      <section className="content-section">
        <div className="content-container">
          <div className="content-left">
            <SplitText
              text="Designing experiences that move people and grow businesses."
              tag="h2"
              className="content-headline"
              splitType="words"
              delay={80}
              duration={0.8}
              from={{ opacity: 0, y: 40 }}
              to={{ opacity: 1, y: 0 }}
              textAlign="left"
            />
          </div>
          <div className="content-right">
            <SplitText
              text="I'm a designer who crafts meaningful experiences that align with business goals, blending clean, modern, and visually appealing design with thoughtful research and strategy."
              tag="p"
              className="content-description"
              splitType="words"
              delay={100}
              duration={0.8}
              from={{ opacity: 0, y: 30 }}
              to={{ opacity: 1, y: 0 }}
              textAlign="left"
            />
            <button className="about-me-btn">
              <span>About me</span>
              <svg className="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Works Section */}
      <section className="works-section">
        <div className="works-container">
          <h2 className="works-title">Works</h2>
          <div className="works-list">
            <div className="work-item" onClick={handleTradeSystemClick}>
              <span className="work-title">Trade Management System.</span>
              <span className="work-category">Design & Development</span>
            </div>
            <div className="work-item" onClick={handleStockMarketClick}>
              <span className="work-title">Stock Market E-Learning Platform.</span>
              <span className="work-category">Progressive Design</span>
            </div>
            <div className="work-item" onClick={handleFoodManagementClick}>
              <span className="work-title">Food Management System.</span>
              <span className="work-category">Design & Development</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer-section">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-left">
              <h2 className="footer-heading">Let's Work Together</h2>
              <div className="contact-info">
                <div className="contact-item">
                  <svg className="contact-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Queens, NY</span>
                </div>
                <div className="contact-item">
                  <svg className="contact-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>subeksha.thapa@outlook.com</span>
                </div>
                <div className="contact-item">
                  <svg className="contact-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>+1 (929)-666-1298</span>
                </div>
              </div>
              <nav className="footer-navigation">
                <a href="/about">About</a>
                <a href="#works" className="active">Works</a>
                <a href="#resume" onClick={handleResumeDownload}>Resume</a>
                <a href="/contact">Contact</a>
              </nav>
            </div>
            <div className="footer-right">
              <button className="contact-me-btn" onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}>
                <span>Contact Me!</span>
                <svg className="contact-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Main App component with routing
function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/trade-management-system" element={<TradeManagementSystem />} />
        <Route path="/stock-market-news-portal" element={<StockMarketNewsPortal />} />
        <Route path="/food-management-system" element={<FoodManagementSystem />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
      </Routes>
    </Router>
  );
}

export default App;
